<template>
  <div class="flex flex-col h-full bg-slate-900 border-t border-slate-800 select-none overflow-hidden">
    <!-- PrimeVue 4 Tabs Container -->
    <Tabs v-model:value="activeTab" class="flex flex-col h-full">
      <!-- Tab Header Bar -->
      <div class="bg-slate-950 border-b border-slate-800 px-2 flex items-center justify-between shrink-0">
        <TabList class="!bg-transparent !border-none">
          <!-- Console Output Tab -->
          <Tab value="console" class="!text-xs !py-2 !px-3 !font-medium">
            <i class="pi pi-terminal mr-1.5 text-sky-400"></i>
            Console Output
            <span v-if="result?.executionTimeMs" class="ml-1.5 text-[10px] text-slate-500 font-mono">
              ({{ result.executionTimeMs }}ms)
            </span>
          </Tab>

          <!-- Matplotlib Plots Tab -->
          <Tab value="plots" class="!text-xs !py-2 !px-3 !font-medium">
            <i class="pi pi-chart-line mr-1.5 text-emerald-400"></i>
            Plots
            <span v-if="result?.plots?.length" class="ml-1 bg-emerald-500/20 text-emerald-300 text-[10px] px-1.5 py-0.2 rounded-full font-mono">
              {{ result.plots.length }}
            </span>
          </Tab>

          <!-- Datasets & Pyodide VFS Tab -->
          <Tab value="datasets" class="!text-xs !py-2 !px-3 !font-medium">
            <i class="pi pi-database mr-1.5 text-amber-400"></i>
            Datasets
            <span v-if="question.datasets?.length" class="ml-1 text-[10px] text-slate-500 font-mono">
              ({{ question.datasets.length }})
            </span>
          </Tab>

          <!-- Reference Solution Tab -->
          <Tab value="reference" class="!text-xs !py-2 !px-3 !font-medium">
            <i class="pi pi-lightbulb mr-1.5 text-amber-300"></i>
            Reference Solution
          </Tab>

          <!-- Checklist Tab -->
          <Tab value="checklist" class="!text-xs !py-2 !px-3 !font-medium">
            <i class="pi pi-check-square mr-1.5 text-purple-400"></i>
            Checklist
            <span class="ml-1.5 text-[10px] px-1.5 py-0.2 rounded-full bg-purple-500/20 text-purple-300 font-mono">
              {{ checkedCount }}/{{ question.checklist.length }}
            </span>
          </Tab>
        </TabList>

        <!-- Right actions -->
        <div class="flex items-center gap-1.5 shrink-0 pr-1">
          <button 
            v-if="activeTab === 'console'"
            type="button"
            @click="$emit('clear-console')"
            class="text-xs text-slate-400 hover:text-slate-200 px-2 py-1 rounded bg-slate-800 hover:bg-slate-750 transition-colors flex items-center space-x-1 cursor-pointer"
          >
            <i class="pi pi-trash text-xs"></i>
            <span class="hidden sm:inline">Clear</span>
          </button>
          <button
            type="button"
            @click="$emit('collapse')"
            class="text-xs text-slate-400 hover:text-amber-300 px-2 py-1 rounded bg-slate-800 hover:bg-slate-750 transition-colors flex items-center space-x-1 cursor-pointer"
            title="Hide Output Panel (Alt+O)"
          >
            <i class="pi pi-chevron-down text-xs"></i>
            <span class="hidden sm:inline">Hide</span>
          </button>
        </div>
      </div>

      <!-- Tab Content Panels -->
      <TabPanels class="flex-1 overflow-y-auto !bg-slate-900 !p-0">
        
        <!-- 1. Console Output Panel -->
        <TabPanel value="console" class="h-full p-4 font-mono text-xs overflow-y-auto">
          <div v-if="!result" class="text-slate-500 py-8 text-center italic">
            Click "Run Code" or press <kbd class="px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-[10px]">Shift+Enter</kbd> to execute Python code in Pyodide.
          </div>

          <div v-else class="space-y-3">
            <!-- Stdout Output -->
            <div v-if="result.stdout" class="space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Standard Output</span>
              <pre class="bg-slate-950 p-3 rounded border border-slate-800 text-slate-200 whitespace-pre-wrap overflow-x-auto leading-relaxed select-text">{{ result.stdout }}</pre>
            </div>

            <!-- Stderr Output -->
            <div v-if="result.stderr" class="space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-amber-500 block">Standard Error</span>
              <pre class="bg-slate-950 p-3 rounded border border-amber-900/40 text-amber-300 whitespace-pre-wrap overflow-x-auto select-text">{{ result.stderr }}</pre>
            </div>

            <!-- Error Exception Traceback -->
            <div v-if="result.error" class="space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-rose-500 block">Python Exception Traceback</span>
              <pre class="bg-rose-950/40 p-3 rounded border border-rose-800/60 text-rose-300 whitespace-pre-wrap overflow-x-auto leading-relaxed select-text">{{ result.error }}</pre>
            </div>

            <!-- Summary Execution Banner -->
            <div class="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-800/80">
              <span>Executed at {{ new Date(result.timestamp).toLocaleTimeString() }}</span>
              <span :class="result.error ? 'text-rose-400' : 'text-emerald-400'">
                {{ result.error ? 'Finished with Error' : 'Completed Successfully' }}
              </span>
            </div>
          </div>
        </TabPanel>

        <!-- 2. Plots Panel -->
        <TabPanel value="plots" class="h-full p-4 overflow-y-auto">
          <div v-if="!result?.plots?.length" class="text-slate-500 py-12 text-center text-xs italic">
            No matplotlib plots generated. Use <code class="bg-slate-800 px-1 py-0.5 rounded">plt.show()</code> or plotting methods to render figures.
          </div>

          <div v-else class="space-y-6 max-w-4xl mx-auto">
            <div 
              v-for="(plotSrc, idx) in result.plots" 
              :key="idx"
              class="bg-slate-950 p-3 rounded-xl border border-slate-800 space-y-2 shadow-lg"
            >
              <div class="flex items-center justify-between text-xs text-slate-400 font-mono">
                <span>Figure {{ idx + 1 }}</span>
                <a 
                  :href="plotSrc" 
                  :download="`unibo_plot_fig_${idx+1}.png`" 
                  class="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 transition-colors flex items-center space-x-1"
                >
                  <i class="pi pi-download text-xs"></i>
                  <span>Download PNG</span>
                </a>
              </div>
              <img :src="plotSrc" :alt="`Matplotlib Figure ${idx+1}`" class="w-full h-auto rounded bg-white p-2" />
            </div>
          </div>
        </TabPanel>

        <!-- 3. Datasets Panel -->
        <TabPanel value="datasets" class="h-full p-4 overflow-y-auto space-y-4">
          <div class="bg-slate-950 p-3 rounded-lg border border-slate-800">
            <h4 class="text-xs font-semibold text-slate-200 mb-1">Pyodide Virtual Filesystem Guide</h4>
            <p class="text-xs text-slate-400 leading-relaxed">
              Dataset files are automatically written into Pyodide's working directory. You can load them directly in Python:
            </p>
            <div class="mt-2 bg-slate-900 p-2 rounded text-xs font-mono text-emerald-400 select-all border border-slate-800">
              import pandas as pd<br/>
              df = pd.read_csv('{{ question.datasets[0]?.name || "dataset.csv" }}')
            </div>
          </div>

          <!-- Question Datasets List -->
          <div class="space-y-2">
            <h4 class="text-xs font-semibold uppercase tracking-wider text-slate-400">Attached Datasets</h4>
            <div 
              v-for="ds in question.datasets" 
              :key="ds.name"
              class="p-3 bg-slate-800/60 rounded-lg border border-slate-700/80 flex items-center justify-between"
            >
              <div class="flex items-center space-x-3">
                <i class="pi pi-file text-amber-400 text-lg"></i>
                <div>
                  <span class="text-xs font-semibold text-slate-200 font-mono block">{{ ds.name }}</span>
                  <span class="text-[10px] text-slate-400 font-mono">Location: /{{ ds.name }} & /data/{{ ds.name }}</span>
                </div>
              </div>

              <div class="flex items-center space-x-2">
                <a 
                  :href="ds.url" 
                  download 
                  class="px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-xs transition-colors flex items-center space-x-1"
                >
                  <i class="pi pi-download text-xs"></i>
                  <span>Download</span>
                </a>
              </div>
            </div>
          </div>

          <!-- Upload Custom File -->
          <div class="p-3 bg-slate-950/80 rounded-lg border border-dashed border-slate-700 text-center space-y-2">
            <i class="pi pi-upload text-slate-400 text-xl block"></i>
            <span class="text-xs text-slate-300 font-medium block">Upload Custom CSV/TXT to Pyodide FS</span>
            <input type="file" @change="handleFileUpload" accept=".csv,.txt,.json" class="text-xs text-slate-400 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-unibo-red file:text-white hover:file:bg-unibo-darkRed cursor-pointer" />
          </div>
        </TabPanel>

        <!-- 4. Reference Solution Panel -->
        <TabPanel value="reference" class="h-full p-4 overflow-y-auto space-y-4">
          <!-- Disclaimer Alert -->
          <div class="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-xs leading-relaxed flex items-start space-x-2.5">
            <i class="pi pi-exclamation-triangle text-amber-400 text-base shrink-0 mt-0.5"></i>
            <div>
              <strong class="font-semibold block mb-0.5">Self-Evaluation Guidance:</strong>
              There is no single right answer. This is one valid reference solution matching University of Bologna exam standards. Your code may differ in algorithm choices, style, or plotting details.
            </div>
          </div>

          <!-- Spoiler Reveal Control -->
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <button 
                @click="showSolution = !showSolution"
                class="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium transition-colors flex items-center space-x-2"
              >
                <i :class="showSolution ? 'pi pi-eye-slash text-rose-400' : 'pi pi-eye text-emerald-400'"></i>
                <span>{{ showSolution ? 'Hide Reference Solution' : 'Reveal Example Solution' }}</span>
              </button>

              <button 
                v-if="showSolution"
                @click="$emit('copy-solution', question.referenceSolution)"
                class="px-3 py-1.5 bg-unibo-red hover:bg-unibo-darkRed text-white rounded-lg text-xs font-medium transition-colors flex items-center space-x-1.5 shadow-sm"
              >
                <i class="pi pi-copy"></i>
                <span>Copy Solution to Editor</span>
              </button>
            </div>

            <!-- Solution Code Block -->
            <div v-if="showSolution" class="bg-slate-950 p-4 rounded-xl border border-slate-800 font-mono text-xs overflow-x-auto text-slate-200 leading-relaxed select-text shadow-inner">
              <pre>{{ question.referenceSolution }}</pre>
            </div>
          </div>
        </TabPanel>

        <!-- 5. Professor Checklist Panel -->
        <TabPanel value="checklist" class="h-full p-4 overflow-y-auto space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-xs font-semibold text-slate-200">Exam Rubric Checklist</h4>
              <p class="text-[11px] text-slate-400">Mark items as you self-evaluate your solution against exam standards.</p>
            </div>
            <span class="text-xs font-mono font-bold text-purple-400 bg-purple-500/10 px-2.5 py-1 rounded border border-purple-500/20">
              {{ checkedCount }} / {{ question.checklist.length }} Done
            </span>
          </div>

          <!-- Progress Bar -->
          <div class="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
            <div 
              class="bg-purple-500 h-full transition-all duration-300"
              :style="{ width: `${(checkedCount / (question.checklist.length || 1)) * 100}%` }"
            ></div>
          </div>

          <!-- Interactive Checkbox List -->
          <div class="space-y-2">
            <label 
              v-for="item in question.checklist" 
              :key="item.id"
              class="flex items-start space-x-3 p-3 rounded-lg bg-slate-800/40 border border-slate-800 hover:bg-slate-800/80 cursor-pointer transition-colors"
            >
              <input 
                type="checkbox" 
                :checked="checkedIds.includes(item.id)"
                @change="toggleCheck(item.id)"
                class="mt-0.5 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 h-4 w-4"
              />
              <span class="text-xs text-slate-300 leading-snug select-none" :class="{ 'line-through opacity-60': checkedIds.includes(item.id) }">
                {{ item.label }}
              </span>
            </label>
          </div>
        </TabPanel>

      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import { Question, ExecutionResult } from '../types';
import { mountDatasetInVFS } from '../services/pyodide';

const props = defineProps<{
  question: Question;
  result?: ExecutionResult;
  checkedIds: string[];
  activeOutputTab: string;
}>();

const emit = defineEmits(['clear-console', 'copy-solution', 'toggle-checklist', 'update:activeOutputTab', 'collapse']);

const activeTab = computed({
  get: () => props.activeOutputTab || 'console',
  set: (val) => emit('update:activeOutputTab', val),
});

const showSolution = ref(false);

const checkedCount = computed(() => {
  return props.checkedIds.length;
});

function toggleCheck(id: string) {
  emit('toggle-checklist', id);
}

async function handleFileUpload(e: Event) {
  const target = e.target as HTMLInputElement;
  if (!target.files?.length) return;
  const file = target.files[0];
  const reader = new FileReader();
  reader.onload = async () => {
    if (reader.result) {
      const arrayBuffer = reader.result as ArrayBuffer;
      await mountDatasetInVFS(file.name, new Uint8Array(arrayBuffer));
      alert(`Mounted ${file.name} into Pyodide virtual filesystem! You can now pd.read_csv('${file.name}') in code.`);
    }
  };
  reader.readAsArrayBuffer(file);
}
</script>
